// React Dependencies
import React, { useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  MenuItem,
  TextField,
} from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { toast } from 'react-toastify';

// Internal Dependencies
import apiService from '../../services.js/apiService';
import MButton from '../common/MButton';
import MText from '../common/MText';
import OverLoading from '../global/OverLoading';
import Colours from '../../styles/colours';
import { IsEmpty } from '../../common/checks';
import { ModerationTypes } from '../../common/constants';
import { Trim } from '../../common/util';

const BanModal = ({ open, onClose, onSuccess }) => {
  const [searchType, setSearchType]             = useState(ModerationTypes.Username);
  const [searchValue, setSearchValue]           = useState('');
  const [searchResult, setSearchResult]         = useState(null);
  const [reason, setReason]                     = useState('');
  const [password, setPassword]                 = useState('');
  const [isSearchingShopper, setIsSearchingShopper] = useState(false);
  const [actioning, setActioning]               = useState(false);
  const isBanDisabled                           = actioning || IsEmpty(password);

  const resetState = () => {
    setSearchType(ModerationTypes.Username);
    setSearchValue('');
    setSearchResult(null);
    setReason('');
    setPassword('');
  };

  const handleClose = () => {
    if (!actioning) {
      resetState();
      onClose();
    }
  };

  const handleSearchBanUser = async () => {
    if (IsEmpty(searchValue)) {
      return;
    }

    setIsSearchingShopper(true);
    setSearchResult(null);
    try {
      const params = { [searchType]: Trim(searchValue) };
      const res = await apiService.getShopperModerationDetails(params);

      if (!IsEmpty(res.rows)) {
        if (res.rows[0].isBanned) {
          toast.error('Shopper is already banned.');
        } else {
          setSearchResult(res.rows[0]);
        }
      } else {
        toast.error('No shopper found with the given criteria.');
      }
    } catch (err) {
      toast.error(err.message || String(err));
    } finally {
      setIsSearchingShopper(false);
    }
  };

  const executeBan = async () => {
    if (IsEmpty(searchResult) || IsEmpty(password)) {
      if (IsEmpty(password)) {
        toast.error('Staff password is required');
      }
      return;
    }
    setActioning(true);

    try {
      const displayName = searchResult.username || `Shopper #${searchResult.id}`;
      await apiService.banShopper(searchResult.id, {
        reason: Trim(reason) || undefined,
        password,
      });
      toast.success(`${displayName} has been banned.`);

      resetState();
      onSuccess();
    } catch (err) {
      toast.error(err.message || String(err));
    } finally {
      setActioning(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { overflowX: 'hidden' },
        }}
      >
        <DialogTitle>Ban a Shopper</DialogTitle>
        <DialogContent style={{ overflowX: 'hidden' }}>
          {!searchResult ? (
            <>
              <DialogContentText>
                Search for an active shopper to ban by exact ID, Username, or Email.
              </DialogContentText>
              <Box display="flex" alignItems="flex-end" mb={2}>
                <Box mr={2} flex={1}>
                  <FormControl fullWidth>
                    <TextField
                      select
                      label="Search By"
                      value={searchType}
                      onChange={(e) => setSearchType(e.target.value)}
                    >
                      <MenuItem value={ModerationTypes.Username}>Username</MenuItem>
                      <MenuItem value={ModerationTypes.Email}>Email</MenuItem>
                      <MenuItem value={ModerationTypes.Id}>Shopper ID</MenuItem>
                    </TextField>
                  </FormControl>
                </Box>
                <Box flex={2}>
                  <FormControl fullWidth>
                    <TextField
                      name="ban-shopper-search"
                      label={`Enter exact ${searchType}`}
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSearchBanUser();
                        }
                      }}
                    />
                  </FormControl>
                </Box>
              </Box>
            </>
          ) : (
            <>
              <DialogContentText style={{ overflowWrap: 'anywhere' }}>
                You are about to ban
                {' '}
                <strong>{searchResult.username}</strong>
                {' '}
                (ID:
                {searchResult.id}
                , Email:
                {searchResult.email}
                ).
                They will be prevented from accessing their account.
              </DialogContentText>
              <Box mt={1} mb={2}>
                <MButton
                  variant="outlined"
                  textColour={Colours.Primary}
                  text="Search another user"
                  onClick={() => {
                    setSearchResult(null);
                  }}
                  disabled={actioning}
                />
              </Box>

              <TextField
                fullWidth
                label="Ban reason (optional)"
                placeholder="e.g. Spam, harassment, fraudulent activity..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                variant="outlined"
                multiline
                rows={2}
                style={{ marginTop: 10, maxWidth: '100%' }}
                InputProps={{ style: { maxWidth: '100%', boxSizing: 'border-box' } }}
                FormHelperTextProps={{ style: { whiteSpace: 'normal' } }}
                helperText="Defaults to 'Violated terms of usage' if left blank. For internal audit only, not shown to the user."
              />

              <Box mt={3}>
                <MText text="Confirm password" size={12} bold />
                <TextField
                  autoComplete="new-password"
                  fullWidth
                  size="small"
                  type="password"
                  label="Enter your staff password to confirm"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  variant="outlined"
                  style={{ marginTop: 10, maxWidth: '100%' }}
                  required
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          {IsEmpty(searchResult) && (
            <Button
              onClick={handleSearchBanUser}
              color="primary"
              variant="contained"
              disabled={isSearchingShopper || IsEmpty(Trim(searchValue))}
            >
              {isSearchingShopper ? 'Confirming...' : 'Confirm'}
            </Button>
          )}
          {!IsEmpty(searchResult) && (
            <Button
              onClick={executeBan}
              style={{
                backgroundColor: isBanDisabled ? Colours.Gray2 : Colours.Crimson,
                color:           Colours.White,
              }}
              variant="contained"
              disabled={isBanDisabled}
            >
              {actioning ? 'Banning...' : 'Confirm'}
            </Button>
          )}
          <Button onClick={handleClose} disabled={actioning}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      {(actioning || isSearchingShopper) && <OverLoading />}
    </>
  );
};

BanModal.propTypes = {
  open:      PropTypes.bool.isRequired,
  onClose:   PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default BanModal;
